import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import { useNavigate } from 'react-router-dom';
import { ReactComponent as ChatbotReportsIcon } from "../../../assets/ChatbotReportsSvgs/ChatbotReportsIcon.svg";
import { ReactComponent as CloseChatbotReportsIcon } from "../../../assets/ChatbotReportsSvgs/CloseChatbotReportsIcon.svg";
import { ReactComponent as SendButtonIcon } from "../../../assets/ChatbotReportsSvgs/SendButtonIconChatbot.svg";
import { ReactComponent as ChatbotWindowCloseIcon } from "../../../assets/ChatbotReportsSvgs/ChatbotWindowCloseIcon.svg";
import { ReactComponent as ExpandChatbotIcon } from "../../../assets/ChatbotReportsSvgs/ExpandChatbotIcon.svg";
import { ReactComponent as MaghilChatbotLogo } from "../../../assets/ChatbotReportsSvgs/MaghilChatbotLogo.svg";
import { ReactComponent as CollapseChatbotIcon } from "../../../assets/ChatbotReportsSvgs/CollapseChatbotIcon.svg";
import { ReactComponent as VoiceInputChatbotIcon } from "../../../assets/ChatbotReportsSvgs/VoiceInuputChatbotIcon.svg";
import { ReactComponent as FileUploadChatbotIcon } from "../../../assets/ChatbotReportsSvgs/FileUploadChatbotIcon.svg";
import { ReactComponent as StopChatbotIcon } from "../../../assets/ChatbotReportsSvgs/StopChatbotIcon.svg";
import { ReactComponent as SuggestionQuestionsDrillDownIcon } from "../../../assets/ChatbotReportsSvgs/SuggestionQuestionsDrillDownIcon.svg";
import { ReactComponent as ChatHistoryIcon } from "../../../assets/ChatbotReportsSvgs/ChatHistoryIcon.svg";
import { ReactComponent as BackButtonChatbotIcon } from "../../../assets/ChatbotReportsSvgs/BackButtonChatbotIcon.svg";
import MetricsMessageBubble from './MetricsMessageBubble';
import InsightsMessageBubble from './InsightsMessageBubble';
import UserQueryMessageBubble from './UserQueryMessageBubble';
import ChatbotResponseMessageBubble from './ChatbotResponseMessageBubble';
import DetailMessageBubble from './DetailMessageBubble';
import SessionFailureMessageBubble from './SessionFailureMessageBubble';
import ChatHistoryListing from './ChatHistoryListing';
import ChatHistory from './ChatHistory';
import { useAuth } from '../../../context/AuthContext';
import { useBranch } from '../../../context/BranchContext';
import UserProfileDropdown, { getInitials } from '../../layout/UserProfileDropdown';
import './style.scss'
import {
    ChatbotReportsPropsInterface,
    ChatHistoryListSuccessResponseInterface,
    ChatHistorySessionInterface,
    ConvMessage,
    ConvMessageData,
    InsightBotInitResponse,
    LocationDetails,
    MerchantInfoSuccessResponseInterface,
} from '../../../interface/ReportsChatbotInterface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
    fetchMerchantInfoRequest,
    startSessionRequest,
    fetchChatHistoryRequest,
    sendQueryRequest,
    retryDataRequest,
    retryInsightsRequest,
    cancelQueryRequest,
} from '../../../redux/reports/ReportsChatbot/ReportsChatbotActions';
import { 
    REPORTS_CHATBOT_API_FAILURE_RESPONSE,
    REPORTS_CHATBOT_CENTER_TEXT, 
    REPORTS_CHATBOT_FOOTER_TEXT, 
    REPORTS_CHATBOT_HEADER_TEXT, 
    REPORTS_CHATBOT_MESSAGE_INPUT_PLACEHOLDER, 
    REPORTS_CHATBOT_NAME, 
    REPORTS_CHATBOT_SUGGESTION_PILLS, 
    REPORTS_CHATBOT_SUGGESTION_PILLS_HEADING,
} from '../../../constants/ChatbotReportsConstants';

const generateId = () => `msg_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

function useMediaQuery(query: string): boolean {
    const [matches, setMatches] = useState(() =>
        typeof window !== 'undefined' ? window.matchMedia(query).matches : false
    );
    useEffect(() => {
        const mql = window.matchMedia(query);
        const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
        mql.addEventListener('change', handler);
        setMatches(mql.matches);
        return () => mql.removeEventListener('change', handler);
    }, [query]);
    return matches;
}

const ChatbotReports = ({
    headerTextReportsChatBot = REPORTS_CHATBOT_HEADER_TEXT,
    centerTextReportsChatBot = REPORTS_CHATBOT_CENTER_TEXT,
    messageInputPlaceholderReportsChatBot = REPORTS_CHATBOT_MESSAGE_INPUT_PLACEHOLDER,
    footerTextReportsChatBot = REPORTS_CHATBOT_FOOTER_TEXT,
    enableSuggestionsReportsChatBot = true,
    suggestionPillsHeadingReportsChatBot = REPORTS_CHATBOT_SUGGESTION_PILLS_HEADING,
    suggestionPillsDataReportsChatBot = REPORTS_CHATBOT_SUGGESTION_PILLS,
    enableVoiceInputReportsChatBot = true,
    enableFileUploadReportsChatBot = true,
    apiFailureRespone = REPORTS_CHATBOT_API_FAILURE_RESPONSE,
    chatbotReportsName = REPORTS_CHATBOT_NAME,
    defaultOpen = false,
    onClose
}: ChatbotReportsPropsInterface) => {

    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { auth, logout } = useAuth();
    const { selectedBranch } = useBranch();

    // ── Redux state ───────────────────────────────────────────────────────────
    const merchantInfo: MerchantInfoSuccessResponseInterface | null = useAppSelector((state) => state.ReportsChatbot.merchantInfo);
    const sessionData: InsightBotInitResponse | null = useAppSelector((state) => state.ReportsChatbot.sessionData);
    const sessionLoading: boolean = useAppSelector((state) => state.ReportsChatbot.sessionLoading);
    const sessionFailure: boolean = useAppSelector((state) => state.ReportsChatbot.sessionFailure);
    const chatHistoryData: ChatHistoryListSuccessResponseInterface | null = useAppSelector((state) => state.ReportsChatbot.chatHistoryData);
    const classifyLoading: boolean = useAppSelector((state) => state.ReportsChatbot.classifyLoading);
    const metricsLoading: boolean = useAppSelector((state) => state.ReportsChatbot.metricsLoading);
    const insightsLoading: boolean = useAppSelector((state) => state.ReportsChatbot.insightsLoading);
    const conversation: ConvMessage[] = useAppSelector((state) => state.ReportsChatbot.conversation);

    // ── Local UI state ────────────────────────────────────────────────────────
    const [isChatbotWindowExpanded, setIsChatbotWindowExpanded] = useState<boolean>(false);
    const [isChatbotWindowOpen, setIsChatbotWindowOpen] = useState<boolean>(defaultOpen);
    const [inputValue, setInputValue] = useState<string>('');
    const [historyView, setHistoryView] = useState<'none' | 'listing' | 'detail'>('none');
    const [selectedHistorySession, setSelectedHistorySession] = useState<ChatHistorySessionInterface | null>(null);

    const scrollBottomRef = useRef<HTMLDivElement>(null);
    const historyEnterLengthRef = useRef<number>(0);

    const locationId = useMemo(
        () => selectedBranch?.id ?? selectedBranch?.locationId ?? '',
        [selectedBranch]
    );
    const locationLabel = useMemo(
        () => selectedBranch?.locationName ?? selectedBranch?.name ?? '',
        [selectedBranch]
    );

    const isMobile = useMediaQuery('(max-width: 768px)');

    const fullName = useMemo(() => auth?.fullName?.trim() ?? '', [auth]);
    const profileDisplayName = useMemo(() => auth?.fullName ?? auth?.userId ?? auth?.userName ?? 'User', [auth]);
    const profileRole = useMemo(() => auth?.role ?? auth?.designation ?? 'Restaurant_Manager', [auth]);
    const profileUser = useMemo(() => ({
        initials: getInitials(profileDisplayName),
        name: profileDisplayName,
        role: profileRole,
        onLogout: () => { logout(); navigate('/login', { replace: true }); },
    }), [profileDisplayName, profileRole, logout, navigate]);


    const pendingTurnRef = useRef<{ query: string; sessionId: string; turnId: string } | null>(null)

    const [dragPos, setDragPos] = useState<{ x: number; y: number } | null>(null)
    const isDraggingRef = useRef(false)



    const locationIds: string[] = useMemo(
        () => merchantInfo?.all_locations?.map((loc: LocationDetails) => loc.locationid) || [],
        [merchantInfo]
    );

    const city = locationLabel?.split(', ')[1] || locationLabel;

    // ── Derived ───────────────────────────────────────────────────────────────
    const isWaitingForResponse =
        classifyLoading ||
        metricsLoading ||
        insightsLoading ||
        (conversation.length > 0 && (() => {
            const last = conversation[conversation.length - 1];
            return (last.type === 'ai-data' && last.status === 'loading') || (last.type === 'ai-insights' && last.status === 'loading');
        })());

    const iconColor =
        inputValue?.length > 0
            ? '#E52333'
            : isWaitingForResponse
                ? '#999'
                : '#a8a8a8';

    // ── Effects ───────────────────────────────────────────────────────────────

    // Auto-scroll to bottom on new messages
    useEffect(() => {
        scrollBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [conversation]);

    // Fetch merchant info when chatbot opens
    useEffect(() => {
        if (!isChatbotWindowOpen || !locationId) return;
        dispatch(fetchMerchantInfoRequest({
            locationid: locationId,
            user_role: auth?.role ?? '',
        }));
    }, [isChatbotWindowOpen, locationId, auth?.role, dispatch]);

    // Start session + fetch history when merchant info is ready
    useEffect(() => {
        if (!isChatbotWindowOpen || !merchantInfo) return;
        dispatch(startSessionRequest({
            user_name: fullName,
            locationid: merchantInfo.locationid,
            merchant_id: merchantInfo.merchant_id,
            all_location: locationIds,
            user_role: merchantInfo.user_role,
        }));
        dispatch(fetchChatHistoryRequest({
            locationid: merchantInfo.locationid,
            user_role: merchantInfo.user_role,
        }));
    }, [isChatbotWindowOpen, merchantInfo, dispatch, fullName, locationIds]);

    // ── Handlers ──────────────────────────────────────────────────────────────

    const getNowTimeLabel = useCallback(() => {
        try {
            return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } catch {
            return '';
        }
    }, []);

    const handleSendMessage = useCallback((text?: string) => {
        const messageText = (text ?? inputValue).trim();
        if (!messageText || isWaitingForResponse) return;

        const canStart =
            isChatbotWindowOpen &&
            !!locationId &&
            !sessionLoading &&
            !!sessionData &&
            !sessionFailure;
        if (!canStart) return;

        const turnId = generateId();
        dispatch(sendQueryRequest({
            query: messageText,
            sessionId: sessionData.session_id,
            turnId,
            locationId,
            apiFailureResponse: apiFailureRespone,
        }));
        setInputValue('');
    }, [
        inputValue,
        isWaitingForResponse,
        isChatbotWindowOpen,
        locationId,
        sessionLoading,
        sessionData,
        sessionFailure,
        dispatch,
        apiFailureRespone,
    ]);

    const handleDataRetry = useCallback((messageId: string) => {
        if (!sessionData?.session_id || !locationId) return;
        dispatch(retryDataRequest({
            messageId,
            sessionId: sessionData.session_id,
            locationId,
            apiFailureResponse: apiFailureRespone,
        }));
    }, [sessionData?.session_id, locationId, dispatch, apiFailureRespone]);

    const handleInsightsRetry = useCallback((messageId: string) => {
        if (!sessionData?.session_id || !locationId) return;
        dispatch(retryInsightsRequest({
            messageId,
            sessionId: sessionData.session_id,
            locationId,
            apiFailureResponse: apiFailureRespone,
        }));
    }, [sessionData?.session_id, locationId, dispatch, apiFailureRespone]);

    const handleRetrySession = useCallback(() => {
        if (!merchantInfo) return;
        dispatch(startSessionRequest({
            user_name: fullName,
            locationid: merchantInfo.locationid,
            merchant_id: merchantInfo.merchant_id,
            all_location: locationIds,
            user_role: merchantInfo.user_role,
        }));
    }, [merchantInfo, fullName, locationIds, dispatch]);

    const handleStopQuery = useCallback(() => {
        dispatch(cancelQueryRequest());
    }, [dispatch]);

    // Reset drag position when chatbot opens
    useEffect(() => {
        if (isChatbotWindowOpen) setDragPos(null);
    }, [isChatbotWindowOpen]);

    const handleToggleDragStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
        const isTouch = 'touches' in e;
        const clientX = isTouch ? (e as React.TouchEvent).touches[0].clientX : (e as React.MouseEvent).clientX;
        const clientY = isTouch ? (e as React.TouchEvent).touches[0].clientY : (e as React.MouseEvent).clientY;
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        const startX = clientX;
        const startY = clientY;
        const elemX = rect.left;
        const elemY = rect.top;
        let dragged = false;

        const onMove = (ev: MouseEvent | TouchEvent) => {
            const cx = 'touches' in ev ? (ev as TouchEvent).touches[0].clientX : (ev as MouseEvent).clientX;
            const cy = 'touches' in ev ? (ev as TouchEvent).touches[0].clientY : (ev as MouseEvent).clientY;
            const dx = cx - startX;
            const dy = cy - startY;
            if (!dragged && (Math.abs(dx) > 5 || Math.abs(dy) > 5)) dragged = true;
            if (dragged) {
                isDraggingRef.current = true;
                setDragPos({
                    x: Math.max(0, Math.min(elemX + dx, window.innerWidth - 64)),
                    y: Math.max(0, Math.min(elemY + dy, window.innerHeight - 64)),
                });
            }
        };

        const onUp = () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
            document.removeEventListener('touchmove', onMove);
            document.removeEventListener('touchend', onUp);
            if (isDraggingRef.current) {
                setTimeout(() => { isDraggingRef.current = false; }, 0);
            }
        };

        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
        document.addEventListener('touchmove', onMove, { passive: false });
        document.addEventListener('touchend', onUp);
    }, []);

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !isWaitingForResponse) handleSendMessage();
    };

    return (
        <>
            {!defaultOpen && (
                <div
                    className="toggle-chatbot-window-container"
                    onMouseDown={!isChatbotWindowOpen ? handleToggleDragStart : undefined}
                    onTouchStart={!isChatbotWindowOpen ? handleToggleDragStart : undefined}
                    style={!isChatbotWindowOpen && dragPos
                        ? { left: dragPos.x, top: dragPos.y, bottom: 'auto', right: 'auto' }
                        : undefined}
                >
                    {!isChatbotWindowOpen ? (<div className="open-chatbot-window">
                        <ChatbotReportsIcon onClick={() => { if (!isDraggingRef.current) setIsChatbotWindowOpen(true); }} />
                    </div>) : (
                        <div className="close-chatbot-window">
                            <CloseChatbotReportsIcon onClick={() => setIsChatbotWindowOpen(false)} />
                        </div>)}
                </div>
            )}
            {isChatbotWindowOpen ? (
                <div className={`reports-chatbot-container ${isChatbotWindowExpanded ? 'expanded' : ''}`}>
                    <div className='reports-chatbot-header-container'>
                        <div className='maghil-insights-logo-and-text-container'>
                            {historyView === 'detail' || historyView === 'listing' ? (<>
                                <BackButtonChatbotIcon className='back-btn-maghil-chatbot' onClick={() => {
                                    if (historyView === 'detail') setHistoryView('listing');
                                    else if (historyView === 'listing') setHistoryView('none');
                                }}/>
                                <div className='maghil-insights-text'>Recent Insights</div>
                            </>) : (<>
                                <MaghilChatbotLogo className="maghil-chatbot-logo" />
                                <div className='maghil-insights-text'>{headerTextReportsChatBot}</div>
                            </>)
                            }
                        </div>
                        <div className='expand-minimize-close-buttons-container'>
                            {chatHistoryData && (
                                <ChatHistoryIcon
                                    className='chatbot-history-icon'
                                    onClick={() => setHistoryView('listing')}
                                />
                            )}
                            {!isChatbotWindowExpanded && !isMobile ?
                                <ExpandChatbotIcon onClick={() => setIsChatbotWindowExpanded(true)} /> : !isMobile ?
                                    <CollapseChatbotIcon onClick={() => setIsChatbotWindowExpanded(false)} /> : <></>}
                            <ChatbotWindowCloseIcon className="chatbot-window-close-icon" onClick={() => defaultOpen ? onClose?.() : setIsChatbotWindowOpen(false)} />
                            <UserProfileDropdown user={profileUser} iconOnly />
                        </div>
                    </div>
                    <div className='reports-chatbot-body-container'>
                        {historyView === 'listing' && (
                            <ChatHistoryListing
                                history={chatHistoryData?.history || []}
                                onSelectSession={(session) => {
                                    historyEnterLengthRef.current = conversation.length;
                                    setSelectedHistorySession(session);
                                    setHistoryView('detail');
                                }}
                                onClose={() => setHistoryView('none')}
                            />
                        )}
                        {historyView === 'detail' && selectedHistorySession && (
                            <ChatHistory
                                session={selectedHistorySession}
                                chatbotName={chatbotReportsName}
                                liveMessages={conversation.slice(historyEnterLengthRef.current)}
                                onSendMessage={handleSendMessage}
                                inputValue={inputValue}
                                setInputValue={setInputValue}
                                isWaitingForResponse={isWaitingForResponse as boolean}
                                iconColor={iconColor}
                                messageInputPlaceholder={messageInputPlaceholderReportsChatBot}
                                footerText={footerTextReportsChatBot}
                                enableVoiceInput={enableVoiceInputReportsChatBot}
                                enableFileUpload={enableFileUploadReportsChatBot}
                                apiFailureResponse={apiFailureRespone}
                                onStop={handleStopQuery}
                                onBack={() => setHistoryView('listing')}
                                onClose={() => setHistoryView('none')}
                            />
                        )}
                        <div className="chat-body-scroll-area" style={{ display: historyView !== 'none' ? 'none' : undefined }}>
                            {conversation.length === 0 && sessionFailure ? (
                                <div className="chat-messages-container">
                                    {locationLabel && (
                                        <div className="date-separator">{city}</div>
                                    )}
                                    <SessionFailureMessageBubble
                                        message={apiFailureRespone}
                                        onRetry={handleRetrySession}
                                    />
                                </div>
                            ) : conversation.length === 0 ? (
                                <div className="chatbot-body-initial-state">
                                    <ChatbotReportsIcon />
                                    <div className='chatbot-body-text'>{centerTextReportsChatBot}</div>
                                </div>
                            ) : (
                                <div className="chat-messages-container">
                                    <div className="date-separator">{city}</div>
                                    {conversation.map((msg) => {
                                        if (msg.type === 'welcome') {
                                            return (
                                                <ChatbotResponseMessageBubble
                                                    key={msg.id}
                                                    text={msg.text}
                                                />
                                            );
                                        }
                                        if (msg.type === 'ai-reply') {
                                            return (
                                                <ChatbotResponseMessageBubble
                                                    key={msg.id}
                                                    text={msg.text}
                                                />
                                            );
                                        }
                                        if (msg.type === 'user') {
                                            return (
                                                <UserQueryMessageBubble
                                                    key={msg.id}
                                                    text={msg.text}
                                                />
                                            );
                                        }
                                        if (msg.type === 'ai-data') {
                                            if (msg.status === 'skip' && !msg.errorMessage) return null;
                                            if (msg.status === 'skip' && msg.errorMessage) {
                                                return (
                                                    <DetailMessageBubble
                                                        key={msg.id}
                                                        sender="ai"
                                                        isFailure={true}
                                                        apiFailureMessage={msg.errorMessage}
                                                        onRetry={() => handleDataRetry(msg.id)}
                                                    />
                                                );
                                            }
                                            if (msg.status === 'metrics') {
                                                return (
                                                    <MetricsMessageBubble
                                                        key={msg.id}
                                                        sender="ai"
                                                        isLoadingMetricsData={false}
                                                        metricsData={msg.data}
                                                        isFailureMetricsData={!!msg.errorMessage}
                                                        apiFailureResponeMetricsData={msg.errorMessage ?? apiFailureRespone}
                                                        onRetry={() => handleDataRetry(msg.id)}
                                                    />
                                                );
                                            }
                                            if (msg.status === 'detail') {
                                                return (
                                                    <DetailMessageBubble
                                                        key={msg.id}
                                                        sender="ai"
                                                        rawResults={msg.data?.raw_results}
                                                        results={msg.data?.results}
                                                        isLoading={false}
                                                        isFailure={!!msg.errorMessage}
                                                        apiFailureMessage={msg.errorMessage ?? apiFailureRespone}
                                                        onRetry={() => handleDataRetry(msg.id)}
                                                    />
                                                );
                                            }
                                            return (
                                                <DetailMessageBubble
                                                    key={msg.id}
                                                    sender="ai"
                                                    isLoading={msg.status === 'loading'}
                                                    onRetry={() => handleDataRetry(msg.id)}
                                                />
                                            );
                                        }
                                        if (msg.type === 'ai-insights') {
                                            if (msg.status === 'skip') return null;
                                            return (
                                                <InsightsMessageBubble
                                                    key={msg.id}
                                                    sender="ai"
                                                    chatbotName={chatbotReportsName}
                                                    insightsData={msg.status === 'success' && msg.data ? msg.data : { session_id: '', insight: '', currency: { code: '', symbol: '' } }}
                                                    isLoadingInsightsData={msg.status === 'loading'}
                                                    isFailureInsightsData={msg.status === 'failure'}
                                                    apiFailureResponeInsightsData={msg.errorMessage ?? apiFailureRespone}
                                                    onRetry={() => handleInsightsRetry(msg.id)}
                                                    timeLabel={getNowTimeLabel()}
                                                    onThumbsUp={() => { }}
                                                    onThumbsDown={() => { }}
                                                />
                                            );
                                        }
                                        return null;
                                    })}
                                    <div ref={scrollBottomRef} />
                                </div>
                            )}
                        </div>

                        <div className="suggestion-and-input-container" style={{ marginTop: 'auto', display: historyView !== 'none' ? 'none' : undefined }}>
                            {enableSuggestionsReportsChatBot && (conversation.length === 0 || (conversation.length === 1 && conversation[0].type === 'welcome')) && (
                                <div className='suggested-for-container'>
                                    {sessionLoading ? (
                                        <>
                                            <div className='suggested-shimmer-text' />
                                            <div className='suggested-for-questions-container'>
                                                {[1, 2, 3].map((i) => (
                                                    <div key={i} className='suggested-shimmer-pill' style={{ width: i === 1 ? 140 : i === 2 ? 120 : 100 }} />
                                                ))}
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div className='suggested-for-text'>{suggestionPillsHeadingReportsChatBot}</div>
                                            <div className='suggested-for-questions-container'>
                                                {(sessionData?.suggested_questions || [])?.map((item, index) => (
                                                    <div
                                                        className='suggested-for-question'
                                                        key={index}
                                                        onClick={() => handleSendMessage(item)}
                                                    >
                                                        <span className='suggested-for-question-text'>{item}</span>
                                                        <SuggestionQuestionsDrillDownIcon className='suggested-for-question-icon' />
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}

                            <div className="message-input-box-container-reports-chatbot">
                                {enableFileUploadReportsChatBot && <FileUploadChatbotIcon />}
                                <input
                                    type="text"
                                    placeholder={messageInputPlaceholderReportsChatBot}
                                    value={inputValue}
                                    onChange={(e) => setInputValue(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                />
                                {enableVoiceInputReportsChatBot && <VoiceInputChatbotIcon />}
                                {isWaitingForResponse ? (
                                    <StopChatbotIcon
                                        className="stop-btn-icon"
                                        onClick={handleStopQuery}
                                    />
                                ) : (
                                    <SendButtonIcon
                                        className="send-btn-icon"
                                        onClick={() => handleSendMessage()}
                                        style={{ color: iconColor }}
                                    />
                                )}
                            </div>
                            <div className="footer-chatbot-container">
                                {footerTextReportsChatBot}
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}
        </>
    )
}

export default ChatbotReports
